import { useCallback, useState } from 'react'
import { Modal } from './Modal'

interface Props {
  /** Shown as the modal heading (the question being answered). */
  label: string
  value: string
  onChange: (v: string) => void
  rows?: number
  placeholder?: string | undefined
  onBlur?: (() => void) | undefined
  /** Fired when either editor (inline or modal) takes focus — callers with an
   *  edit-buffer guard (e.g. WAFR ItemCard) use it to block poll reseeds. */
  onFocus?: (() => void) | undefined
  /** Applied to the inline textarea (callers keep their existing styling). */
  className?: string | undefined
  disabled?: boolean | undefined
  /** Applied to the inline textarea (preserves existing field test ids). The
   *  expand button + modal derive their ids from it. */
  testId?: string | undefined
}

/**
 * A textarea with a corner "expand" button that opens the same field in a
 * roomy modal (question at the top), for the cramped multi-line intake/proposal
 * inputs. The inline textarea and the modal textarea share value+onChange, so
 * edits in either are live; closing the modal fires onBlur (so list-style
 * canonicalization still runs). Both editors spell-check (never autoCorrect,
 * so domain terms like AgentCore are flagged but never silently rewritten).
 */
export function ExpandableTextarea({ label, value, onChange, rows = 6, placeholder, onBlur, onFocus, className, disabled, testId }: Props) {
  const [open, setOpen] = useState(false)

  // Stable identity for close handling and for callers that compare callbacks.
  const handleClose = useCallback(() => {
    setOpen(false)
    onBlur?.()
  }, [onBlur])

  return (
    <div style={{ position: 'relative' }}>
      <textarea
        value={value}
        rows={rows}
        placeholder={placeholder}
        data-testid={testId}
        spellCheck
        className={className}
        onChange={(e) => onChange(e.target.value)}
        onFocus={onFocus}
        onBlur={onBlur}
        disabled={disabled}
        style={{ width: '100%', paddingRight: '1.9rem' }}
      />
      <button
        type="button"
        aria-label={`Expand "${label}"`}
        title="Expand to a larger editor"
        data-testid={testId ? `${testId}-expand` : 'textarea-expand'}
        onClick={() => setOpen(true)}
        disabled={disabled}
        style={{
          position: 'absolute',
          top: 6,
          right: 6,
          padding: '0.1rem 0.3rem',
          lineHeight: 1,
          fontSize: '0.95rem',
          background: 'var(--surface)',
          opacity: 0.9,
          border: '1px solid var(--border)',
          borderRadius: 4,
          cursor: 'pointer',
          color: 'var(--muted)',
        }}
      >
        ⤢
      </button>

      <Modal
        open={open}
        onClose={handleClose}
        title={label}
        maxWidth={780}
        testId={testId ? `${testId}-modal` : 'textarea-modal'}
        ariaLabel={`Edit ${label}`}
      >
        <textarea
          autoFocus
          value={value}
          placeholder={placeholder}
          data-testid={testId ? `${testId}-modal-input` : 'textarea-modal-input'}
          spellCheck
          onChange={(e) => onChange(e.target.value)}
          onFocus={onFocus}
          disabled={disabled}
          // Size to the viewport so it fits inside the modal without forcing the
          // modal body to scroll (no scrollbar by default); the textarea grows a
          // scrollbar only once the text itself overflows 55vh.
          style={{ width: '100%', height: '55vh', minHeight: '12rem', resize: 'vertical', fontSize: '1rem', lineHeight: 1.5, boxSizing: 'border-box' }}
        />
      </Modal>
    </div>
  )
}
